/**
 * UDF reader (ECMA-167, OSTA UDF 1.02 / 2.01 / 2.50 / 2.60).
 *
 * UDF is the filesystem on every modern installer ISO ≥4 GB and on Blu-ray.
 * Most online ISO extractors ignore it and fall back to a tiny ISO9660 stub,
 * silently missing 99% of the actual file tree. We parse it properly.
 *
 * Pipeline:
 *   1. AVDP at sector 256 / size-256 / 512 → MVDS extent
 *   2. MVDS scan → PVD (1), PD (5), LVD (6), USD (7), Terminating (8)
 *   3. LVD → File Set Descriptor extent → FSD parse → root ICB
 *   4. Root FE/EFE → directory File Identifier Descriptors (FIDs)
 *   5. FID extracts → recursive descent (cycle-guarded by lba)
 *
 * Coverage:
 *   - File Entry (tag 261) and Extended File Entry (tag 266)
 *   - Short and Long Allocation Descriptors
 *   - Multi-extent files (>4 GB via continuation extents)
 *   - dstring 8-bit (ASCII) and 16-bit (UCS-2BE) names
 *   - Directory walk via FIDs
 *
 * Deferred to v2:
 *   - Virtual partition map type 1 (CD-R packet writing)
 *   - Metadata partition map type 2 (UDF 2.50+ in-fs metadata)
 *   - Sparable partition map type 1.5 (CD-RW remapping)
 *   - Encrypted UDF
 *   We surface a `partial` flag with reason so the UI can warn.
 *
 * Surface: window.__DiskImage.Udf
 *   var udf = new Udf(reader);
 *   await udf.mount() → { lvd, fsd, root, version, partial?, partition }
 *   udf.tree() → root Node (same shape as Iso9660)
 *   udf.streamFile(node) → ReadableStream
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.Udf) return;

    var errors = w.__DiskImage.errors || {};
    var CorruptedImageError = errors.CorruptedImageError || function (m, d) { var e = new Error(m); e.name = 'CorruptedImageError'; e.details = d; return e; };
    var UnsupportedFilesystemError = errors.UnsupportedFilesystemError || function (m, d) { var e = new Error(m); e.name = 'UnsupportedFilesystemError'; e.details = d; return e; };

    var SECTOR = 2048;

    var TAG = {
        PrimaryVolumeDescriptor: 1,
        AnchorVolumeDescriptor: 2,
        VolumeDescriptorPointer: 3,
        ImpUseVolumeDescriptor: 4,
        PartitionDescriptor: 5,
        LogicalVolumeDescriptor: 6,
        UnallocatedSpaceDescriptor: 7,
        TerminatingDescriptor: 8,
        LogicalVolumeIntegrityDescriptor: 9,
        FileSetDescriptor: 256,
        FileIdentifierDescriptor: 257,
        AllocationExtentDescriptor: 258,
        IndirectEntry: 259,
        TerminalEntry: 260,
        FileEntry: 261,
        ExtendedAttributeHeaderDescriptor: 262,
        UnallocatedSpaceEntry: 263,
        SpaceBitmapDescriptor: 264,
        PartitionIntegrityEntry: 265,
        ExtendedFileEntry: 266
    };

    function _u16le(b, off) { return b[off] | (b[off + 1] << 8); }
    function _u32le(b, off) { return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0); }
    function _u64le(b, off) {
        var lo = _u32le(b, off);
        var hi = _u32le(b, off + 4);
        return hi * 0x100000000 + lo;
    }

    function _ascii(b, off, len) {
        var s = '';
        for (var i = 0; i < len; i++) {
            var c = b[off + i];
            if (c === 0) break;
            s += String.fromCharCode(c);
        }
        return s.replace(/\s+$/, '');
    }

    /** Parse the 16-byte UDF tag (descriptor tag, ECMA-167 7.2). */
    function _parseTag(b, off) {
        if (off + 16 > b.length) return null;
        return {
            id: _u16le(b, off),
            descVersion: _u16le(b, off + 2),
            checksum: b[off + 4],
            tagSerial: _u16le(b, off + 6),
            descCRC: _u16le(b, off + 8),
            descCRCLength: _u16le(b, off + 10),
            tagLocation: _u32le(b, off + 12)
        };
    }

    /** Decode a UDF d-string (ECMA-167 1/7.2). First byte = compID, last byte = used length. */
    function _decodeDstring(b, off, fieldLen) {
        if (fieldLen <= 0) return '';
        var usedLen = b[off + fieldLen - 1] & 0xFF;
        if (usedLen <= 1 || usedLen > fieldLen) usedLen = fieldLen;
        return _decodeCs0(b, off, usedLen);
    }

    /**
     * Decode `len` bytes of OSTA CS0 d-characters: compression id + the units.
     * A File Identifier is exactly this — its length comes from the FID's L_FI,
     * and it has NO trailing length byte. It used to be read as a d-string, so
     * the name's own LAST byte — the last letter of its extension — was taken
     * for its length: '3' of .mp3 is 51, so every name ending in .mp3 longer
     * than 50 Latin or 25 Cyrillic characters came back cut there, extension
     * gone, and two tracks sharing a long beginning extracted onto one name
     * (measured 25.09.2026 on «Кино — Группа крови (концертная запись 1988).mp3
     * → «Кино — Группа крови (конц»).
     */
    function _decodeCs0(b, off, len) {
        var compId = b[off];
        var dataStart = off + 1;
        var dataLen = len - 1;
        if (dataLen <= 0) return '';
        if (compId === 8) {
            // 8-bit characters (ISO 8859-1 / ASCII)
            var s8 = '';
            for (var i = 0; i < dataLen; i++) {
                var c = b[dataStart + i];
                if (c === 0) break;
                s8 += String.fromCharCode(c);
            }
            return s8;
        } else if (compId === 16) {
            // UCS-2BE
            var byteLen = dataLen;
            return new TextDecoder('utf-16be', { fatal: false })
                .decode(b.subarray(dataStart, dataStart + byteLen));
        }
        return '';
    }

    /** Long Allocation Descriptor (ECMA-167 14.14.2): 16 bytes. */
    function _parseLongAd(b, off) {
        var rawLen = _u32le(b, off);
        var type = (rawLen >>> 30) & 0x03;
        var len = rawLen & 0x3FFFFFFF;
        var lbn = _u32le(b, off + 4);
        var prn = _u16le(b, off + 8);
        return { type: type, length: len, logicalBlockNumber: lbn, partitionRef: prn };
    }

    /** Short Allocation Descriptor (ECMA-167 14.14.1): 8 bytes. */
    function _parseShortAd(b, off) {
        var rawLen = _u32le(b, off);
        var type = (rawLen >>> 30) & 0x03;
        var len = rawLen & 0x3FFFFFFF;
        var pos = _u32le(b, off + 4);
        return { type: type, length: len, position: pos };
    }

    function Udf(reader) {
        this.reader = reader;
        this.avdp = null;
        this.lvd = null;
        this.pd = null;
        this.fsd = null;
        this.root = null;
        this.version = null;
        this.partition = null;
        this.partial = false;
        this.partialReason = null;
    }

    Udf.prototype.mount = function () {
        var self = this;
        return self._findAvdp().then(function (avdp) {
            if (!avdp) throw UnsupportedFilesystemError('No AVDP found at sectors 256/end-256/512', { detected: 'udf-missing-avdp' });
            self.avdp = avdp;
            return self._readMvds(avdp.mvdsLba, avdp.mvdsLength);
        }).then(function () {
            if (!self.lvd) throw CorruptedImageError('No Logical Volume Descriptor in MVDS');
            if (!self.pd) throw CorruptedImageError('No Partition Descriptor in MVDS');
            return self._readFsd();
        }).then(function () {
            if (!self.fsd) throw CorruptedImageError('No File Set Descriptor');
            return self._mountRoot();
        }).then(function () {
            return {
                avdp: self.avdp,
                lvd: self.lvd,
                pd: self.pd,
                fsd: self.fsd,
                root: self.root,
                version: self.version,
                partition: self.partition,
                partial: self.partial,
                partialReason: self.partialReason
            };
        });
    };

    /** Try AVDP at sector 256, last_lba-256, and 512. */
    Udf.prototype._findAvdp = function () {
        var self = this;
        var totalSectors = Math.floor(self.reader.size / SECTOR);
        var candidates = [256];
        if (totalSectors > 256) candidates.push(totalSectors - 1);
        if (totalSectors > 512) candidates.push(totalSectors - 256);
        candidates.push(512);
        var unique = candidates.filter(function (v, i, a) { return a.indexOf(v) === i; });

        function step(i) {
            if (i >= unique.length) return null;
            var lba = unique[i];
            return self.reader.bytes(lba * SECTOR, SECTOR).then(function (b) {
                var tag = _parseTag(b, 0);
                if (tag && tag.id === TAG.AnchorVolumeDescriptor) {
                    var mvdsLength = _u32le(b, 16);
                    var mvdsLba = _u32le(b, 20);
                    var rvdsLength = _u32le(b, 24);
                    var rvdsLba = _u32le(b, 28);
                    return { lba: lba, mvdsLba: mvdsLba, mvdsLength: mvdsLength, rvdsLba: rvdsLba, rvdsLength: rvdsLength };
                }
                return step(i + 1);
            }).catch(function () { return step(i + 1); });
        }
        return step(0);
    };

    /** Read all descriptors in MVDS until terminator. */
    Udf.prototype._readMvds = function (mvdsLba, mvdsLength) {
        var self = this;
        var sectorsToRead = Math.ceil(mvdsLength / SECTOR);
        var maxScan = Math.min(sectorsToRead, 64); // sanity cap

        function step(i) {
            if (i >= maxScan) return;
            return self.reader.bytes((mvdsLba + i) * SECTOR, SECTOR).then(function (b) {
                var tag = _parseTag(b, 0);
                if (!tag) return step(i + 1);
                switch (tag.id) {
                    case TAG.TerminatingDescriptor: return;
                    case TAG.PrimaryVolumeDescriptor:
                        self.pvd = _parseUdfPvd(b);
                        break;
                    case TAG.LogicalVolumeDescriptor:
                        self.lvd = _parseLvd(b);
                        // Detect UDF version from domain identifier suffix
                        if (self.lvd.domainId && self.lvd.domainIdSuffix) {
                            var v = self.lvd.domainIdSuffix;
                            if (v.length >= 2) {
                                var udfRev = v[0] | (v[1] << 8);
                                self.version = ((udfRev >> 8) & 0xFF) + '.' + ((udfRev & 0xFF).toString(16).padStart(2, '0'));
                            }
                        }
                        break;
                    case TAG.PartitionDescriptor:
                        self.pd = _parsePartitionDescriptor(b);
                        break;
                    case TAG.UnallocatedSpaceDescriptor:
                    case TAG.ImpUseVolumeDescriptor:
                    default:
                        break;
                }
                return step(i + 1);
            }).catch(function () { return step(i + 1); });
        }
        return step(0);
    };

    function _parseUdfPvd(b) {
        return {
            volumeDescSeqNumber: _u32le(b, 16),
            primaryVolumeDescNumber: _u32le(b, 20),
            volumeIdentifier: _decodeDstring(b, 24, 32),
            volumeSequenceNumber: _u16le(b, 56),
            maximumVolumeSequenceNumber: _u16le(b, 58),
            interchangeLevel: _u16le(b, 60),
            maximumInterchangeLevel: _u16le(b, 62),
            characterSetList: _u32le(b, 64),
            maximumCharacterSetList: _u32le(b, 68),
            volumeSetIdentifier: _decodeDstring(b, 72, 128)
        };
    }

    function _parseLvd(b) {
        // ECMA-167 14.6
        var lv = {
            volumeDescSeqNumber: _u32le(b, 16),
            descCharSet: b.subarray(20, 84),
            logicalVolumeId: _decodeDstring(b, 84, 128),
            logicalBlockSize: _u32le(b, 212),
            domainIdFlags: b[216],
            domainId: _ascii(b, 217, 23),
            domainIdSuffix: b.subarray(240, 248),
            logicalVolumeContentsUse: b.subarray(248, 248 + 16),
            mapTableLength: _u32le(b, 264),
            numberOfPartitionMaps: _u32le(b, 268),
            implementationId: _ascii(b, 272, 32),
            partitionMaps: []
        };
        // FSD location is encoded as a long_ad in logicalVolumeContentsUse
        lv.fsdLongAd = _parseLongAd(b, 248);

        // Parse partition maps
        var off = 440;
        var mapsRead = 0;
        var maxMaps = lv.numberOfPartitionMaps;
        while (mapsRead < maxMaps && off + 2 <= b.length) {
            var mapType = b[off];
            var mapLen = b[off + 1];
            if (mapLen === 0 || off + mapLen > b.length) break;
            var pm = { type: mapType, length: mapLen };
            if (mapType === 1) {
                // Type 1 partition map (simple)
                pm.volumeSeqNumber = _u16le(b, off + 2);
                pm.partitionNumber = _u16le(b, off + 4);
            } else if (mapType === 2) {
                // Type 2 (virtual / sparable / metadata) — partial
                pm.partitionTypeId = _ascii(b, off + 4, 23);
                pm.volumeSeqNumber = _u16le(b, off + 36);
                pm.partitionNumber = _u16le(b, off + 38);
            }
            lv.partitionMaps.push(pm);
            mapsRead++;
            off += mapLen;
        }
        return lv;
    }

    function _parsePartitionDescriptor(b) {
        return {
            volumeDescSeqNumber: _u32le(b, 16),
            partitionFlags: _u16le(b, 20),
            partitionNumber: _u16le(b, 22),
            partitionContents: _ascii(b, 25, 23),
            partitionContentsUse: b.subarray(48, 48 + 128),
            accessType: _u32le(b, 184),
            partitionStartingLocation: _u32le(b, 188),
            partitionLength: _u32le(b, 192)
        };
    }

    Udf.prototype._readFsd = function () {
        var self = this;
        if (!self.lvd || !self.lvd.fsdLongAd) return Promise.resolve();
        var fsdAd = self.lvd.fsdLongAd;
        // Resolve partition reference to physical sector
        self.partition = self._resolvePartition(fsdAd.partitionRef);
        if (!self.partition) {
            // Some images use partition map type 2 we don't fully support; flag partial
            self.partial = true;
            self.partialReason = 'unresolved_partition_ref:' + fsdAd.partitionRef;
            return Promise.resolve();
        }
        var fsdSector = self.partition.startingLocation + fsdAd.logicalBlockNumber;
        return self.reader.bytes(fsdSector * SECTOR, SECTOR).then(function (b) {
            var tag = _parseTag(b, 0);
            if (!tag || tag.id !== TAG.FileSetDescriptor) {
                throw CorruptedImageError('Expected File Set Descriptor at LBA ' + fsdSector + ', got tag ' + (tag && tag.id));
            }
            var rootIcb = _parseLongAd(b, 400);
            self.fsd = {
                tag: tag,
                logicalVolumeIdentifier: _decodeDstring(b, 304, 128),
                fileSetIdentifier: _decodeDstring(b, 304 + 128, 32),
                rootDirectoryICB: rootIcb,
                domainId: _ascii(b, 432 + 1, 23)
            };
        });
    };

    Udf.prototype._resolvePartition = function (partRef) {
        if (!this.lvd) return null;
        var pmaps = this.lvd.partitionMaps || [];
        if (partRef >= pmaps.length) return null;
        var pm = pmaps[partRef];
        if (pm.type !== 1) {
            // We don't handle virtual/sparable/metadata partitions in v1
            return null;
        }
        if (!this.pd || this.pd.partitionNumber !== pm.partitionNumber) return null;
        return {
            startingLocation: this.pd.partitionStartingLocation,
            length: this.pd.partitionLength,
            mapType: pm.type
        };
    };

    Udf.prototype._mountRoot = function () {
        var self = this;
        if (!self.fsd || !self.partition) return Promise.resolve();
        var rootAd = self.fsd.rootDirectoryICB;
        return self._readFileEntry(rootAd).then(function (fe) {
            if (!fe || !fe.isDirectory) throw CorruptedImageError('Root ICB is not a directory');
            var root = {
                name: '',
                path: '/',
                isDir: true,
                lba: self._absLba(rootAd.partitionRef, rootAd.logicalBlockNumber),
                size: fe.informationLength,
                _udfFe: fe,
                children: [],
                udf: true,
                _parent: null
            };
            self.root = root;
            return self._populate(root);
        });
    };

    Udf.prototype._absLba = function (partRef, lbn) {
        var p = this._resolvePartition(partRef);
        if (!p) return -1;
        return p.startingLocation + lbn;
    };

    /**
     * Read a File Entry (FE / EFE) at the given long_ad and return its parsed form.
     */
    Udf.prototype._readFileEntry = function (longAd) {
        var self = this;
        var lba = self._absLba(longAd.partitionRef, longAd.logicalBlockNumber);
        if (lba < 0) return Promise.resolve(null);
        return self.reader.bytes(lba * SECTOR, SECTOR).then(function (b) {
            var tag = _parseTag(b, 0);
            if (!tag) return null;
            if (tag.id === TAG.FileEntry) return _parseFe(b, false, self);
            if (tag.id === TAG.ExtendedFileEntry) return _parseFe(b, true, self);
            if (tag.id === TAG.IndirectEntry) {
                var indAd = _parseLongAd(b, 16);
                return self._readFileEntry(indAd);
            }
            return null;
        });
    };

    function _parseFe(b, extended, udf) {
        // ICB tag at offset 16, 20 bytes
        var icbStrategy = _u16le(b, 18);
        var icbStrategyParam = _u16le(b, 20);
        var fileType = b[16 + 11]; // ECMA-167 14.6.6
        var icbFlags = _u16le(b, 16 + 18);
        var fe = {
            isDirectory: fileType === 4,
            isSymlink: fileType === 12,
            isStreamDir: fileType === 13,
            fileType: fileType,
            uid: _u32le(b, 36),
            gid: _u32le(b, 40),
            permissions: _u32le(b, 44),
            fileLinkCount: _u16le(b, 48),
            recordFormat: b[50],
            recordDisplayAttrs: b[51],
            recordLength: _u32le(b, 52),
            informationLength: _u64le(b, 56),
            allocationDescriptors: [],
            extended: !!extended,
            allocationType: icbFlags & 0x07
        };
        var pos;
        var lengthOfEa;
        var lengthOfAd;
        if (extended) {
            // EFE: information length at 56, logicalBlocksRecorded at 64,
            // accessTime/modTime/createTime/attrTime each 12 bytes starting at 72
            lengthOfEa = _u32le(b, 208);
            lengthOfAd = _u32le(b, 212);
            pos = 216 + lengthOfEa;
        } else {
            lengthOfEa = _u32le(b, 168);
            lengthOfAd = _u32le(b, 172);
            pos = 176 + lengthOfEa;
        }
        // Allocation descriptors per allocationType:
        //   0 = short_ad (8 bytes)
        //   1 = long_ad (16 bytes)
        //   2 = ext_ad (20 bytes)
        //   3 = embedded data (informationLength bytes inline)
        if (fe.allocationType === 3) {
            fe.embeddedData = b.slice(pos, pos + Math.min(lengthOfAd, b.length - pos));
        } else if (fe.allocationType === 0) {
            for (var o = pos; o + 8 <= pos + lengthOfAd && o + 8 <= b.length; o += 8) {
                var ad = _parseShortAd(b, o);
                fe.allocationDescriptors.push({ type: ad.type, length: ad.length, lbn: ad.position, partRef: udf ? (udf.fsd && udf.fsd.rootDirectoryICB.partitionRef) : 0 });
            }
        } else if (fe.allocationType === 1) {
            for (var o2 = pos; o2 + 16 <= pos + lengthOfAd && o2 + 16 <= b.length; o2 += 16) {
                var lad = _parseLongAd(b, o2);
                fe.allocationDescriptors.push({ type: lad.type, length: lad.length, lbn: lad.logicalBlockNumber, partRef: lad.partitionRef });
            }
        }
        return fe;
    }

    /**
     * Walk a directory FE's data and parse FIDs into child Nodes.
     */
    Udf.prototype._populate = function (node, opts) {
        opts = opts || {};
        var maxDepth = opts.maxDepth != null ? opts.maxDepth : 32;
        var seen = opts._seen || new Set();
        if (!node.isDir) return Promise.resolve(node);
        if (seen.has(node.lba)) return Promise.resolve(node);
        seen.add(node.lba);

        var self = this;
        return self._readFileData(node._udfFe).then(function (buf) {
            var off = 0;
            var children = [];
            while (off + 38 <= buf.length) {
                var tag = _parseTag(buf, off);
                if (!tag || tag.id !== TAG.FileIdentifierDescriptor) break;
                var fid = _parseFid(buf, off);
                if (!fid) break;
                off += fid.totalLength;
                if (fid.isDeleted) continue;
                if (fid.isParent) continue;

                var childAbsLba = self._absLba(fid.icb.partitionRef, fid.icb.logicalBlockNumber);
                var child = {
                    name: fid.fileIdentifier,
                    path: (node.path === '/' ? '/' : node.path + '/') + fid.fileIdentifier,
                    isDir: fid.isDirectory,
                    lba: childAbsLba,
                    size: 0, // updated after FE read
                    _parent: node,
                    udf: true,
                    children: fid.isDirectory ? [] : null,
                    _udfFid: fid
                };
                children.push(child);
            }
            // Resolve each child's FE for size + alloc descriptors
            var p = Promise.resolve();
            children.forEach(function (ch) {
                p = p.then(function () {
                    return self._readFileEntry(ch._udfFid.icb).then(function (fe) {
                        if (fe) {
                            ch._udfFe = fe;
                            ch.size = fe.informationLength;
                            ch.mode = fe.permissions;
                            ch.uid = fe.uid;
                            ch.gid = fe.gid;
                            ch.isDir = fe.isDirectory;
                            ch.isSymlink = fe.isSymlink;
                        }
                    }).catch(function () {});
                });
            });
            return p.then(function () {
                node.children = children;
                if (maxDepth > 0) {
                    var subPromise = Promise.resolve();
                    children.forEach(function (ch) {
                        if (ch.isDir) {
                            subPromise = subPromise.then(function () {
                                return self._populate(ch, { maxDepth: maxDepth - 1, _seen: seen });
                            });
                        }
                    });
                    return subPromise;
                }
            });
        });
    };

    function _parseFid(b, off) {
        // ECMA-167 14.4: FID = tag(16) + fileVersion(2) + chars(1) + lenFI(1) + icb(16) + lenIU(2) + impUse(lenIU) + fi(lenFI) + padding to 4
        if (off + 38 > b.length) return null;
        var fileVersion = _u16le(b, off + 16);
        var chars = b[off + 18];
        var lenFI = b[off + 19];
        var icb = _parseLongAd(b, off + 20);
        var lenIU = _u16le(b, off + 36);
        var iuStart = off + 38;
        var fiStart = iuStart + lenIU;
        var fiEnd = fiStart + lenFI;
        var totalLen = (fiEnd - off + 3) & ~3; // pad to 4-byte alignment
        var name = '';
        if (lenFI > 0 && fiEnd <= b.length) {
            // First byte of FI is compression code
            name = _decodeCs0(b, fiStart, lenFI);
        }
        return {
            fileVersion: fileVersion,
            characteristics: chars,
            isDirectory: !!(chars & 0x02),
            isDeleted: !!(chars & 0x04),
            isParent: !!(chars & 0x08),
            isMetadata: !!(chars & 0x10),
            icb: icb,
            fileIdentifier: name,
            totalLength: totalLen
        };
    }

    /**
     * Read raw bytes pointed to by a file entry's allocation descriptors.
     * For multi-extent files, concatenates extents in order.
     */
    Udf.prototype._readFileData = function (fe) {
        if (!fe) return Promise.resolve(new Uint8Array(0));
        if (fe.allocationType === 3) {
            return Promise.resolve(fe.embeddedData || new Uint8Array(0));
        }
        var self = this;
        var totalLen = fe.informationLength;
        var ads = fe.allocationDescriptors || [];
        if (ads.length === 0 || totalLen === 0) return Promise.resolve(new Uint8Array(0));

        var out = new Uint8Array(totalLen);
        var written = 0;
        var p = Promise.resolve();
        ads.forEach(function (ad) {
            if (ad.type === 2) return; // not allocated
            if (ad.length === 0) return;
            p = p.then(function () {
                if (written >= totalLen) return;
                var absLba = self._absLba(ad.partRef, ad.lbn);
                if (absLba < 0) return;
                var take = Math.min(ad.length, totalLen - written);
                return self.reader.bytes(absLba * SECTOR, take).then(function (b) {
                    out.set(b, written);
                    written += b.length;
                });
            });
        });
        return p.then(function () { return out.subarray(0, written); });
    };

    Udf.prototype.streamFile = function (node) {
        if (!node || node.isDir) throw new TypeError('Udf.streamFile: node is not a file');
        var fe = node._udfFe;
        if (!fe) throw new Error('Udf.streamFile: node has no FE');
        if (fe.allocationType === 3) {
            // Embedded data
            return new ReadableStream({
                start: function (c) { c.enqueue(fe.embeddedData || new Uint8Array(0)); c.close(); }
            });
        }
        var self = this;
        var ads = (fe.allocationDescriptors || []).filter(function (a) { return a.type !== 2 && a.length > 0; });
        var idx = 0;
        var inExtentOff = 0;
        return new ReadableStream({
            pull: function (c) {
                while (idx < ads.length) {
                    var ad = ads[idx];
                    var absLba = self._absLba(ad.partRef, ad.lbn);
                    if (absLba < 0) { idx++; inExtentOff = 0; continue; }
                    var remaining = ad.length - inExtentOff;
                    if (remaining <= 0) { idx++; inExtentOff = 0; continue; }
                    var CHUNK = 1024 * 1024;
                    var take = Math.min(remaining, CHUNK);
                    return self.reader.bytes(absLba * SECTOR + inExtentOff, take).then(function (b) {
                        c.enqueue(b);
                        inExtentOff += take;
                        if (inExtentOff >= ad.length) { idx++; inExtentOff = 0; }
                    }).catch(function (err) { c.error(err); });
                }
                c.close();
            }
        });
    };

    Udf.prototype.iterate = function () {
        var nodes = [];
        function walk(n) {
            nodes.push(n);
            if (n.children) n.children.forEach(walk);
        }
        if (this.root) (this.root.children || []).forEach(walk);
        return nodes;
    };

    Udf.prototype.findByPath = function (path) {
        if (!path || path === '/') return this.root;
        var parts = path.replace(/^\/+/, '').split('/');
        var node = this.root;
        for (var i = 0; i < parts.length; i++) {
            if (!node || !node.children) return null;
            var p = parts[i];
            var found = null;
            for (var j = 0; j < node.children.length; j++) {
                var c = node.children[j];
                if (c.name === p) { found = c; break; }
            }
            if (!found) return null;
            node = found;
        }
        return node;
    };

    w.__DiskImage.Udf = Udf;
})(window);
